import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, RefreshCw, Lock, Trash2, Plus } from "lucide-react";
import { FadeUp, PageShell, Eyebrow } from "@/components/Reveal";
import { useContent } from "@/context/ContentContext";
import { Field, ImageField, Section, LineArrayField, ListEditor } from "@/components/admin/fields";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;
const STATUSES = ["New", "Contacted", "In Review", "Closed"];

const TABS = [
  { id: "enquiries", label: "Enquiries" },
  { id: "home", label: "Home Page" },
  { id: "turnkey", label: "Turnkey Page" },
  { id: "about", label: "About Page" },
  { id: "projects", label: "Projects" },
  { id: "services", label: "Services" },
  { id: "contact", label: "Contact" },
  { id: "settings", label: "Settings" },
];

const selectCls =
  "w-full bg-obsidian border border-white/10 focus:border-bronze outline-none px-4 py-3 text-sm text-bone font-light";

const GalleryEditor = ({ value, onChange, token }) => (
  <div>
    <label className="text-[10px] uppercase tracking-[0.25em] text-muted2 block mb-2">Gallery Images</label>
    <div className="space-y-3">
      {(value || []).map((url, i) => (
        <div key={i} className="flex items-start gap-3">
          <div className="flex-1">
            <ImageField value={url} onChange={(v) => onChange(value.map((u, idx) => (idx === i ? v : u)))} token={token} testid={`gallery-image-input-${i}`} />
          </div>
          <button type="button" onClick={() => onChange(value.filter((_, idx) => idx !== i))} className="mt-2 text-muted2 hover:text-red-400 transition-colors duration-300" aria-label="Remove image">
            <Trash2 size={14} />
          </button>
        </div>
      ))}
      <button type="button" onClick={() => onChange([...(value || []), ""])} className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-bronze border border-bronze/40 px-4 py-2 transition-all duration-300 hover:bg-bronze hover:text-obsidian">
        <Plus size={12} /> Add Gallery Image
      </button>
    </div>
  </div>
);

const TagsField = ({ label, value, onChange }) => (
  <Field label={label} value={(value || []).join(", ")} onChange={(v) => onChange(v.split(",").map((s) => s.trim()).filter(Boolean))} />
);

export default function Admin() {
  const [token, setToken] = useState(() => sessionStorage.getItem("sld_admin_token") || "");
  const [password, setPassword] = useState("");

  const login = async (e) => {
    e.preventDefault();
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password });
      sessionStorage.setItem("sld_admin_token", data.token);
      setToken(data.token);
      toast.success("Welcome back.");
    } catch {
      toast.error("Invalid admin password.");
    }
  };

  const logout = () => {
    sessionStorage.removeItem("sld_admin_token");
    setToken("");
  };

  if (!token) {
    return (
      <PageShell testid="admin-page">
        <section className="min-h-screen flex items-center justify-center px-4">
          <FadeUp className="w-full max-w-sm">
            <form data-testid="admin-login-form" onSubmit={login} className="border border-white/[0.07] p-10 bg-ink/40">
              <span className="w-12 h-12 border border-bronze/40 flex items-center justify-center text-bronze mb-8">
                <Lock size={18} />
              </span>
              <Eyebrow className="mb-3">Studio Admin</Eyebrow>
              <h1 className="font-serif text-2xl font-light text-bone mb-8">Site Console</h1>
              <label className="text-[10px] uppercase tracking-[0.3em] text-muted2">Admin Password</label>
              <input
                data-testid="admin-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="luxury-input mt-2 mb-8"
              />
              <button type="submit" data-testid="admin-login-btn" className="w-full bg-bronze text-obsidian text-[11px] uppercase tracking-[0.25em] px-8 py-4 transition-all duration-500 hover:bg-bronze-light">
                Unlock Console
              </button>
            </form>
          </FadeUp>
        </section>
      </PageShell>
    );
  }

  return <Panel token={token} logout={logout} />;
}

function Panel({ token, logout }) {
  const content = useContent();
  const [tab, setTab] = useState("enquiries");
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState("");
  const [enquiries, setEnquiries] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pw, setPw] = useState({ current: "", next: "", confirm: "" });
  const [pwSaving, setPwSaving] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };
  // Hostinger blocks PUT/PATCH/DELETE — send POST and declare the real method.
  const ov = (method) => ({ ...headers, "X-HTTP-Method-Override": method });

  useEffect(() => {
    const { __refresh, ...data } = content;
    if (data.projects && Array.isArray(data.projects)) setDraft(JSON.parse(JSON.stringify(data)));
  }, [content]);

  const fetchEnquiries = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/enquiries`, { headers });
      setEnquiries(Array.isArray(data) ? data : []);
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error("Session expired — please log in again.");
        logout();
      } else {
        toast.error("Could not load enquiries — tap Refresh to try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEnquiries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async (key) => {
    setSaving(key);
    try {
      await axios.post(`${API}/admin/content/${key}`, { value: draft[key] }, { headers: ov("PUT") });
      content.__refresh();
      toast.success("Saved — live on the site.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed.");
    } finally {
      setSaving("");
    }
  };

  const reset = async (key) => {
    try {
      await axios.post(`${API}/admin/content/${key}`, {}, { headers: ov("DELETE") });
      content.__refresh();
      toast.success("Reset to default content.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reset failed.");
    }
  };

  const set = (key) => (val) => setDraft((d) => ({ ...d, [key]: val }));
  const up = (key, field) => (v) => set(key)({ ...draft[key], [field]: v });

  const updateStatus = async (id, status) => {
    try {
      await axios.post(`${API}/admin/enquiries/${id}`, { status }, { headers: ov("PATCH") });
      setEnquiries((prev) => prev.map((e) => (e.id === id ? { ...e, status } : e)));
      toast.success(`Marked as ${status}.`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update status.");
    }
  };

  const changePassword = async () => {
    if (!pw.current || !pw.next || !pw.confirm) {
      toast.error("Fill in all three password fields.");
      return;
    }
    if (pw.next !== pw.confirm) {
      toast.error("New passwords do not match.");
      return;
    }
    if (pw.next.length < 8) {
      toast.error("New password must be at least 8 characters.");
      return;
    }
    setPwSaving(true);
    try {
      await axios.post(
        `${API}/admin/password`,
        { current_password: pw.current, new_password: pw.next, confirm_password: pw.confirm },
        { headers }
      );
      toast.success("Password updated — use the new password next time you log in.");
      setPw({ current: "", next: "", confirm: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update password.");
    } finally {
      setPwSaving(false);
    }
  };

  if (!draft) {
    return (
      <PageShell testid="admin-page">
        <section className="min-h-screen flex items-center justify-center">
          <p className="text-sm text-muted2 font-light">Loading console…</p>
        </section>
      </PageShell>
    );
  }

  return (
    <PageShell testid="admin-page">
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-36 pb-24 min-h-screen">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <FadeUp>
            <Eyebrow className="mb-3">Studio Admin</Eyebrow>
            <h1 className="font-serif text-3xl sm:text-4xl font-light text-bone">Site Console</h1>
          </FadeUp>
          <div className="flex items-center gap-3">
            <button data-testid="admin-refresh-btn" onClick={() => { content.__refresh(); fetchEnquiries(); }} className="w-11 h-11 border border-white/15 flex items-center justify-center text-bone/70 hover:border-bronze hover:text-bronze transition-colors duration-500" aria-label="Refresh">
              <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
            </button>
            <button data-testid="admin-logout-btn" onClick={logout} className="w-11 h-11 border border-white/15 flex items-center justify-center text-bone/70 hover:border-bronze hover:text-bronze transition-colors duration-500" aria-label="Logout">
              <LogOut size={16} />
            </button>
          </div>
        </div>

        <div className="flex gap-2 overflow-x-auto pb-2 mb-10 border-b border-white/[0.07]" data-testid="admin-tabs">
          {TABS.map((t) => (
            <button
              key={t.id}
              data-testid={`admin-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`shrink-0 text-[10px] uppercase tracking-[0.25em] px-5 py-3 border-b-2 transition-all duration-300 ${
                tab === t.id ? "border-bronze text-bronze" : "border-transparent text-bone/50 hover:text-bone"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === "enquiries" && (
          <div>
            {enquiries && enquiries.length === 0 && (
              <p data-testid="admin-empty-state" className="text-sm text-muted2 font-light border border-white/[0.07] p-10 text-center">
                No enquiries yet — submissions from the contact form will appear here.
              </p>
            )}
            {enquiries && enquiries.length > 0 && (
              <div className="overflow-x-auto border border-white/[0.07]">
                <table data-testid="admin-enquiries-table" className="w-full text-left min-w-[820px]">
                  <thead>
                    <tr className="border-b border-white/[0.07] bg-ink/60">
                      {["Received", "Name", "Contact", "Location / Plot", "Budget", "Message", "Status"].map((h) => (
                        <th key={h} className="text-[10px] uppercase tracking-[0.25em] text-muted2 font-medium px-5 py-4">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {enquiries.map((e) => (
                      <tr key={e.id} data-testid={`enquiry-row-${e.id}`} className="border-b border-white/[0.05] hover:bg-bronze/[0.03] transition-colors duration-300 align-top">
                        <td className="px-5 py-4 font-mono text-[11px] text-muted2 whitespace-nowrap">
                          {e.created_at ? new Date(e.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—"}
                        </td>
                        <td className="px-5 py-4 text-sm text-bone whitespace-nowrap">{e.name}</td>
                        <td className="px-5 py-4 text-xs text-stone2">
                          <div>{e.email}</div>
                          {e.phone && <div className="text-muted2 mt-1">{e.phone}</div>}
                        </td>
                        <td className="px-5 py-4 text-xs text-stone2">
                          <div>{e.location || e.project_type || "—"}</div>
                          {(e.plot_area || e.scope) && (
                            <div className="text-muted2 mt-1">{[e.plot_area, e.scope].filter(Boolean).join(" · ")}</div>
                          )}
                        </td>
                        <td className="px-5 py-4 text-xs text-stone2 whitespace-nowrap">{e.budget || "—"}</td>
                        <td className="px-5 py-4 text-xs text-stone2 max-w-xs"><p className="line-clamp-3">{e.message}</p></td>
                        <td className="px-5 py-4">
                          <select
                            data-testid={`enquiry-status-${e.id}`}
                            value={e.status}
                            onChange={(ev) => updateStatus(e.id, ev.target.value)}
                            className="bg-obsidian border border-bronze/30 text-bronze text-[10px] uppercase tracking-[0.2em] px-3 py-2 outline-none cursor-pointer"
                          >
                            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                          </select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {tab === "home" && (
          <div className="space-y-8">
            <Section title="Hero Banner" desc="Top of the home page" testid="section-hero" saving={saving === "hero"} onSave={() => save("hero")} onReset={() => reset("hero")}>
              <div className="grid sm:grid-cols-2 gap-5">
                <Field label="Eyebrow" testid="hero-eyebrow" value={draft.hero.eyebrow} onChange={up("hero", "eyebrow")} />
                <Field label="Side Text" testid="hero-sidetext" value={draft.hero.sideText} onChange={up("hero", "sideText")} />
                <Field label="Headline — Line 1" testid="hero-line1" value={draft.hero.line1} onChange={up("hero", "line1")} />
                <Field label="Headline — Line 2 (italic)" testid="hero-line2" value={draft.hero.line2} onChange={up("hero", "line2")} />
                <Field label="Headline — Line 3" testid="hero-line3" value={draft.hero.line3} onChange={up("hero", "line3")} />
                <Field label="Primary Button" testid="hero-cta1" value={draft.hero.ctaPrimary} onChange={up("hero", "ctaPrimary")} />
                <Field label="Secondary Button" testid="hero-cta2" value={draft.hero.ctaSecondary} onChange={up("hero", "ctaSecondary")} />
              </div>
              <Field label="Subtext" textarea testid="hero-subtext" value={draft.hero.subtext} onChange={up("hero", "subtext")} />
              <ImageField label="Banner Image" testid="hero-image" value={draft.hero.image} onChange={up("hero", "image")} token={token} />
            </Section>

            <Section title="Who We Are — Intro" testid="section-intro" saving={saving === "intro"} onSave={() => save("intro")} onReset={() => reset("intro")}>
              <div className="grid sm:grid-cols-2 gap-5">
                <Field label="Eyebrow" value={draft.intro.eyebrow} onChange={up("intro", "eyebrow")} />
                <Field label="Small Tag" value={draft.intro.tag} onChange={up("intro", "tag")} />
              </div>
              <Field label="Statement" textarea testid="intro-statement" value={draft.intro.statement} onChange={up("intro", "statement")} />
            </Section>

            <Section title="Call-to-Action Banner" testid="section-cta" saving={saving === "cta"} onSave={() => save("cta")} onReset={() => reset("cta")}>
              <div className="grid sm:grid-cols-2 gap-5">
                <Field label="Eyebrow" value={draft.cta.eyebrow} onChange={up("cta", "eyebrow")} />
                <Field label="Button Text" value={draft.cta.button} onChange={up("cta", "button")} />
                <Field label="Title — First Part" value={draft.cta.titleA} onChange={up("cta", "titleA")} />
                <Field label="Title — Italic Part" value={draft.cta.titleB} onChange={up("cta", "titleB")} />
              </div>
            </Section>

            <Section title="Service Journey Strip" desc="The scrolling strip below the hero — one trade per line" testid="section-marquee" saving={saving === "marqueeItems"} onSave={() => save("marqueeItems")} onReset={() => reset("marqueeItems")}>
              <LineArrayField label="Marquee Items" testid="marquee-items" value={draft.marqueeItems} onChange={set("marqueeItems")} />
            </Section>

            <Section title="Our Values" desc="The three value chapters on the home page" testid="section-manifesto" saving={saving === "manifesto"} onSave={() => save("manifesto")} onReset={() => reset("manifesto")}>
              <ListEditor
                items={draft.manifesto}
                onChange={set("manifesto")}
                makeNew={() => ({ num: String(draft.manifesto.length + 1).padStart(2, "0"), title: "", text: "", image: "" })}
                addLabel="Add Value"
                itemTitle={(v, i) => v.title || `Value ${i + 1}`}
                testid="manifesto-editor"
                renderItem={(v, update) => (
                  <div className="space-y-5">
                    <div className="grid sm:grid-cols-2 gap-5">
                      <Field label="Number" value={v.num} onChange={(val) => update({ num: val })} />
                      <Field label="Title" value={v.title} onChange={(val) => update({ title: val })} />
                    </div>
                    <Field label="Text" textarea value={v.text} onChange={(val) => update({ text: val })} />
                    <ImageField label="Value Image" value={v.image} onChange={(val) => update({ image: val })} token={token} />
                  </div>
                )}
              />
            </Section>

            <Section title="Plot to Possession — Journey" testid="section-journey" saving={saving === "journey"} onSave={() => save("journey")} onReset={() => reset("journey")}>
              <div className="grid sm:grid-cols-2 gap-5">
                <Field label="Eyebrow" value={draft.journey.eyebrow} onChange={up("journey", "eyebrow")} />
                <Field label="Title" value={draft.journey.title} onChange={up("journey", "title")} />
              </div>
              <Field label="Message" textarea value={draft.journey.message} onChange={up("journey", "message")} />
              <LineArrayField label="Journey Steps (one per line)" value={draft.journey.steps} onChange={(v) => up("journey", "steps")(v)} />
            </Section>

            <Section title="Why Choose Sai Leela Decor" testid="section-whyus" saving={saving === "whyUs"} onSave={() => save("whyUs")} onReset={() => reset("whyUs")}>
              <ListEditor
                items={draft.whyUs}
                onChange={set("whyUs")}
                makeNew={() => ({ num: String(draft.whyUs.length + 1).padStart(2, "0"), title: "", text: "" })}
                addLabel="Add Reason"
                itemTitle={(w, i) => w.title || `Reason ${i + 1}`}
                testid="whyus-editor"
                renderItem={(w, update) => (
                  <div className="space-y-5">
                    <div className="grid sm:grid-cols-2 gap-5">
                      <Field label="Number" value={w.num} onChange={(v) => update({ num: v })} />
                      <Field label="Title" value={w.title} onChange={(v) => update({ title: v })} />
                    </div>
                    <Field label="Text" textarea value={w.text} onChange={(v) => update({ text: v })} />
                  </div>
                )}
              />
            </Section>

            <Section title="Brand Film Section" desc="Paste a YouTube/Vimeo embed URL later to replace the placeholder" testid="section-video" saving={saving === "videoSection"} onSave={() => save("videoSection")} onReset={() => reset("videoSection")}>
              <div className="grid sm:grid-cols-2 gap-5">
                <Field label="Eyebrow" value={draft.videoSection.eyebrow} onChange={up("videoSection", "eyebrow")} />
                <Field label="Title" value={draft.videoSection.title} onChange={up("videoSection", "title")} />
              </div>
              <Field label="Note (shown until a video is added)" textarea value={draft.videoSection.note} onChange={up("videoSection", "note")} />
              <Field label="Video Embed URL (optional)" testid="video-url" value={draft.videoSection.url} onChange={up("videoSection", "url")} />
            </Section>

            <Section title="Bungalow Cost Section" testid="section-cost" saving={saving === "costSection"} onSave={() => save("costSection")} onReset={() => reset("costSection")}>
              <div className="grid sm:grid-cols-2 gap-5">
                <Field label="Eyebrow" value={draft.costSection.eyebrow} onChange={up("costSection", "eyebrow")} />
                <Field label="Title" value={draft.costSection.title} onChange={up("costSection", "title")} />
                <Field label="Estimate Card Text" value={draft.costSection.sub} onChange={up("costSection", "sub")} />
                <Field label="Button Text" value={draft.costSection.cta} onChange={up("costSection", "cta")} />
              </div>
              <Field label="Intro" textarea value={draft.costSection.intro} onChange={up("costSection", "intro")} />
              <LineArrayField label="Cost Factors (one per line)" value={draft.costSection.factors} onChange={(v) => up("costSection", "factors")(v)} />
            </Section>

            <Section title="Statistics" testid="section-stats" saving={saving === "stats"} onSave={() => save("stats")} onReset={() => reset("stats")}>
              <ListEditor
                items={draft.stats}
                onChange={set("stats")}
                makeNew={() => ({ value: "", label: "" })}
                addLabel="Add Stat"
                itemTitle={(s, i) => s.label || `Stat ${i + 1}`}
                testid="stats-editor"
                renderItem={(s, update) => (
                  <div className="grid sm:grid-cols-2 gap-5">
                    <Field label="Value" value={s.value} onChange={(v) => update({ value: v })} />
                    <Field label="Label" value={s.label} onChange={(v) => update({ label: v })} />
                  </div>
                )}
              />
            </Section>

            <Section title="Testimonials" testid="section-testimonials" saving={saving === "testimonials"} onSave={() => save("testimonials")} onReset={() => reset("testimonials")}>
              <ListEditor
                items={draft.testimonials}
                onChange={set("testimonials")}
                makeNew={() => ({ quote: "", author: "", project: "" })}
                addLabel="Add Testimonial"
                itemTitle={(t, i) => t.author || `Testimonial ${i + 1}`}
                testid="testimonials-editor"
                renderItem={(t, update) => (
                  <div className="space-y-5">
                    <Field label="Quote" textarea value={t.quote} onChange={(v) => update({ quote: v })} />
                    <div className="grid sm:grid-cols-2 gap-5">
                      <Field label="Author" value={t.author} onChange={(v) => update({ author: v })} />
                      <Field label="Project" value={t.project} onChange={(v) => update({ project: v })} />
                    </div>
                  </div>
                )}
              />
            </Section>
          </div>
        )}

        {tab === "turnkey" && (
          <div className="space-y-8">
            <Section title="What We Handle — Scope Grid" desc="One item per line" testid="section-turnkey-services" saving={saving === "turnkeyServices"} onSave={() => save("turnkeyServices")} onReset={() => reset("turnkeyServices")}>
              <LineArrayField label="Scope Items" value={draft.turnkeyServices} onChange={set("turnkeyServices")} />
            </Section>

            <Section title="Construction Quality Stages" testid="section-quality" saving={saving === "qualityStages"} onSave={() => save("qualityStages")} onReset={() => reset("qualityStages")}>
              <ListEditor
                items={draft.qualityStages}
                onChange={set("qualityStages")}
                makeNew={() => ({ title: "", image: "" })}
                addLabel="Add Stage"
                itemTitle={(q, i) => q.title || `Stage ${i + 1}`}
                testid="quality-editor"
                renderItem={(q, update) => (
                  <div className="space-y-5">
                    <Field label="Stage Title" value={q.title} onChange={(v) => update({ title: v })} />
                    <ImageField label="Stage Image" value={q.image} onChange={(v) => update({ image: v })} token={token} />
                  </div>
                )}
              />
            </Section>

            <Section title="Process Steps" desc="Shared with the About page" testid="section-process2" saving={saving === "process"} onSave={() => save("process")} onReset={() => reset("process")}>
              <ListEditor
                items={draft.process}
                onChange={set("process")}
                makeNew={() => ({ num: String(draft.process.length + 1).padStart(2, "0"), title: "", text: "" })}
                addLabel="Add Step"
                itemTitle={(p, i) => p.title || `Step ${i + 1}`}
                testid="process-editor-turnkey"
                renderItem={(p, update) => (
                  <div className="space-y-5">
                    <div className="grid sm:grid-cols-2 gap-5">
                      <Field label="Number" value={p.num} onChange={(v) => update({ num: v })} />
                      <Field label="Title" value={p.title} onChange={(v) => update({ title: v })} />
                    </div>
                    <Field label="Text" textarea value={p.text} onChange={(v) => update({ text: v })} />
                  </div>
                )}
              />
            </Section>
          </div>
        )}

        {tab === "about" && (
          <div className="space-y-8">
            <Section title="Story" testid="section-about" saving={saving === "about"} onSave={() => save("about")} onReset={() => reset("about")}>
              <Field label="Lead Paragraph" textarea testid="about-storylead" value={draft.about.storyLead} onChange={up("about", "storyLead")} />
              <Field label="Body Paragraph" textarea value={draft.about.storyBody} onChange={up("about", "storyBody")} />
              <Field label="Quote" textarea value={draft.about.quote} onChange={up("about", "quote")} />
              <ImageField label="Story Image" value={draft.about.storyImage} onChange={up("about", "storyImage")} token={token} />
            </Section>

            <Section title="Clientele" desc="One name per line" testid="section-clients" saving={saving === "clients"} onSave={() => save("clients")} onReset={() => reset("clients")}>
              <LineArrayField label="Clients" value={draft.clients} onChange={set("clients")} />
            </Section>

            <Section title="Residential CHS Projects" desc="One name per line" testid="section-residential" saving={saving === "residentialClients"} onSave={() => save("residentialClients")} onReset={() => reset("residentialClients")}>
              <LineArrayField label="Residential Clients" value={draft.residentialClients} onChange={set("residentialClients")} />
            </Section>

            <Section title="Turnkey Collaborators" desc="One name per line" testid="section-consultants" saving={saving === "consultants"} onSave={() => save("consultants")} onReset={() => reset("consultants")}>
              <LineArrayField label="Consultants" value={draft.consultants} onChange={set("consultants")} />
            </Section>

            <Section title="Process Steps" testid="section-process" saving={saving === "process"} onSave={() => save("process")} onReset={() => reset("process")}>
              <ListEditor
                items={draft.process}
                onChange={set("process")}
                makeNew={() => ({ num: String(draft.process.length + 1).padStart(2, "0"), title: "", text: "" })}
                addLabel="Add Step"
                itemTitle={(p, i) => p.title || `Step ${i + 1}`}
                testid="process-editor"
                renderItem={(p, update) => (
                  <div className="space-y-5">
                    <div className="grid sm:grid-cols-2 gap-5">
                      <Field label="Number" value={p.num} onChange={(v) => update({ num: v })} />
                      <Field label="Title" value={p.title} onChange={(v) => update({ title: v })} />
                    </div>
                    <Field label="Text" textarea value={p.text} onChange={(v) => update({ text: v })} />
                  </div>
                )}
              />
            </Section>
          </div>
        )}

        {tab === "projects" && (
          <Section title="Projects" desc="Add, edit or remove portfolio projects — changes go live on save" testid="section-projects" saving={saving === "projects"} onSave={() => save("projects")} onReset={() => reset("projects")}>
            <ListEditor
              items={draft.projects}
              onChange={set("projects")}
              makeNew={() => ({
                id: `project-${Date.now()}`,
                title: "New Project",
                category: "Farmhouse",
                location: "",
                duration: "",
                cost: "",
                architect: "",
                scope: "Turnkey Construction",
                plot_area: "",
                builtup_area: "",
                status: "Completed",
                image: "",
                gallery: [],
                tags: [],
                description: [""],
              })}
              addLabel="Add Project"
              itemTitle={(p, i) => `${String(i + 1).padStart(2, "0")} — ${p.title}`}
              testid="projects-editor"
              renderItem={(p, update) => (
                <div className="space-y-5">
                  <div className="grid sm:grid-cols-2 gap-5">
                    <Field label="Title" value={p.title} onChange={(v) => update({ title: v })} />
                    <div>
                      <label className="text-[10px] uppercase tracking-[0.25em] text-muted2 block mb-2">Category</label>
                      <select className={selectCls} value={p.category} onChange={(e) => update({ category: e.target.value })}>
                        <option>Farmhouse</option>
                        <option>Private Villa</option>
                        <option>Residential</option>
                        <option>Commercial</option>
                      </select>
                    </div>
                    <Field label="Location" value={p.location} onChange={(v) => update({ location: v })} />
                    <Field label="Duration" value={p.duration} onChange={(v) => update({ duration: v })} />
                    <Field label="Project Cost" value={p.cost} onChange={(v) => update({ cost: v })} />
                    <Field label="Architect" value={p.architect} onChange={(v) => update({ architect: v })} />
                    <Field label="Scope" value={p.scope} onChange={(v) => update({ scope: v })} />
                    <Field label="Plot Area" value={p.plot_area} onChange={(v) => update({ plot_area: v })} />
                    <Field label="Built-up Area" value={p.builtup_area} onChange={(v) => update({ builtup_area: v })} />
                    <div>
                      <label className="text-[10px] uppercase tracking-[0.25em] text-muted2 block mb-2">Status</label>
                      <select className={selectCls} value={p.status} onChange={(e) => update({ status: e.target.value })}>
                        <option>Completed</option>
                        <option>Ongoing</option>
                      </select>
                    </div>
                  </div>
                  <ImageField label="Cover Image" value={p.image} onChange={(v) => update({ image: v })} token={token} />
                  <GalleryEditor value={p.gallery} onChange={(v) => update({ gallery: v })} token={token} />
                  <TagsField label="Tags (comma separated)" value={p.tags} onChange={(v) => update({ tags: v })} />
                  <Field
                    label="Description (blank line between paragraphs)"
                    textarea
                    value={(p.description || []).join("\n\n")}
                    onChange={(v) => update({ description: v.split(/\n\s*\n/).map((s) => s.trim()).filter(Boolean) })}
                  />
                </div>
              )}
            />
          </Section>
        )}

        {tab === "services" && (
          <Section title="Services" testid="section-services" saving={saving === "services"} onSave={() => save("services")} onReset={() => reset("services")}>
            <ListEditor
              items={draft.services}
              onChange={set("services")}
              makeNew={() => ({ num: String(draft.services.length + 1).padStart(2, "0"), title: "", desc: "", image: "", tags: [] })}
              addLabel="Add Service"
              itemTitle={(s, i) => `${s.num} — ${s.title || "New Service"}`}
              testid="services-editor"
              renderItem={(s, update) => (
                <div className="space-y-5">
                  <div className="grid sm:grid-cols-2 gap-5">
                    <Field label="Number" value={s.num} onChange={(v) => update({ num: v })} />
                    <Field label="Title" value={s.title} onChange={(v) => update({ title: v })} />
                  </div>
                  <Field label="Description" textarea value={s.desc} onChange={(v) => update({ desc: v })} />
                  <ImageField label="Service Image" value={s.image} onChange={(v) => update({ image: v })} token={token} />
                  <TagsField label="Tags (comma separated)" value={s.tags} onChange={(v) => update({ tags: v })} />
                </div>
              )}
            />
          </Section>
        )}


        {tab === "contact" && (
          <div className="space-y-8">
          <Section title="Contact Details" desc="Shown on the Contact page and footer" testid="section-contact" saving={saving === "contact"} onSave={() => save("contact")} onReset={() => reset("contact")}>
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label="Phone" testid="contact-phone-field" value={draft.contact.phone} onChange={up("contact", "phone")} />
              <Field label="Hours" value={draft.contact.hours} onChange={up("contact", "hours")} />
              <Field label="Email — Primary" value={draft.contact.email} onChange={up("contact", "email")} />
              <Field label="Email — Secondary" value={draft.contact.email2} onChange={up("contact", "email2")} />
            </div>
            <Field label="Address" textarea testid="contact-address-field" value={draft.contact.address} onChange={up("contact", "address")} />
            <TagsField label="Cities (comma separated)" value={draft.contact.cities} onChange={up("contact", "cities")} />
          </Section>

          <Section title="Social Media Links" desc="Shown in the footer and on the Contact page" testid="section-socials" saving={saving === "socials"} onSave={() => save("socials")} onReset={() => reset("socials")}>
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label="Instagram URL" testid="socials-instagram" value={draft.socials.instagram} onChange={up("socials", "instagram")} />
              <Field label="Pinterest URL" testid="socials-pinterest" value={draft.socials.pinterest} onChange={up("socials", "pinterest")} />
              <Field label="LinkedIn URL" testid="socials-linkedin" value={draft.socials.linkedin} onChange={up("socials", "linkedin")} />
              <Field label="WhatsApp URL (wa.me link)" testid="socials-whatsapp" value={draft.socials.whatsapp} onChange={up("socials", "whatsapp")} />
            </div>
          </Section>
          </div>
        )}

        {tab === "settings" && (
          <div className="max-w-lg border border-white/[0.07] bg-ink/30 p-6 sm:p-8 space-y-6" data-testid="section-settings">
            <div>
              <h3 className="font-serif text-xl font-light text-bone">Change Password</h3>
              <p className="mt-2 text-xs text-muted2 font-light leading-relaxed">
                Updates the admin password on the server. Use the new password the next time you log in.
              </p>
            </div>
            <div className="space-y-5">
              <div>
                <label className="text-[10px] uppercase tracking-[0.25em] text-muted2 block mb-2">Current Password</label>
                <input type="password" data-testid="settings-current-password" value={pw.current} onChange={(e) => setPw({ ...pw, current: e.target.value })} className="w-full bg-obsidian border border-white/10 focus:border-bronze outline-none px-4 py-3 text-sm text-bone font-light transition-colors duration-300" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.25em] text-muted2 block mb-2">New Password</label>
                <input type="password" data-testid="settings-new-password" value={pw.next} onChange={(e) => setPw({ ...pw, next: e.target.value })} className="w-full bg-obsidian border border-white/10 focus:border-bronze outline-none px-4 py-3 text-sm text-bone font-light transition-colors duration-300" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.25em] text-muted2 block mb-2">Confirm New Password</label>
                <input type="password" data-testid="settings-confirm-password" value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} className="w-full bg-obsidian border border-white/10 focus:border-bronze outline-none px-4 py-3 text-sm text-bone font-light transition-colors duration-300" />
              </div>
            </div>
            <button type="button" data-testid="settings-save-password-btn" onClick={changePassword} disabled={pwSaving} className="inline-flex items-center gap-2 bg-bronze text-obsidian text-[10px] uppercase tracking-[0.2em] px-5 py-2.5 transition-colors duration-300 hover:bg-bronze-light disabled:opacity-50">
              {pwSaving ? "Updating…" : "Update Password"}
            </button>
          </div>
        )}
      </section>
    </PageShell>
  );
}
